
import { useState, FormEvent } from 'react';

export default function CommercialCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'office',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Basic client‑side validation
    if (!formData.name || !formData.email || !formData.phone) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('projectType', formData.projectType);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6bl9qiohb161tfd7ksg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', projectType: 'office', message: '' });
      } else {
        // Try to extract a helpful error message
        const errorText = await response.text();
        console.error('Form submission failed:', errorText);
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Network or unexpected error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 md:py-24 lg:py-28 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="space-y-6 md:space-y-8">
            <p className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">
              Get Started
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2A2A2A] leading-tight">
              Elevate Your<br />
              <span className="font-serif text-[#D4AF37]">Commercial Space</span>
            </h2>
            <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
              Request a consultation for your commercial project in Johannesburg. Our team will assess your
              space, understand your business needs, and deliver a comprehensive proposal.
            </p>
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-md">
                <p className="text-2xl md:text-3xl font-bold text-[#D4AF37] mb-1">150+</p>
                <p className="text-xs md:text-sm text-gray-600">Commercial Projects</p>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-md">
                <p className="text-2xl md:text-3xl font-bold text-[#D4AF37] mb-1">98%</p>
                <p className="text-xs md:text-sm text-gray-600">On-Time Delivery</p>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-md">
                <p className="text-2xl md:text-3xl font-bold text-[#D4AF37] mb-1">10yr</p>
                <p className="text-xs md:text-sm text-gray-600">Structural Warranty</p>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-md">
                <p className="text-2xl md:text-3xl font-bold text-[#D4AF37] mb-1">Zero</p>
                <p className="text-xs md:text-sm text-gray-600">Hidden Costs</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10">
            <form
              id="commercial-inquiry-form"
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-4 md:space-y-5"
            >
              <div>
                <label htmlFor="comm-name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="comm-name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="comm-email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="comm-email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  placeholder="you@company.com"
                />
              </div>

              <div>
                <label htmlFor="comm-phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="comm-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  placeholder="+27 XX XXX XXXX"
                />
              </div>

              <div>
                <label htmlFor="comm-type" className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Type
                </label>
                <select
                  id="comm-type"
                  name="projectType"
                  value={formData.projectType}
                  onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="office">Office Renovation</option>
                  <option value="retail">Retail Fit-Out</option>
                  <option value="restaurant">Restaurant / Hospitality</option>
                  <option value="medical">Medical / Wellness</option>
                  <option value="tenant">Tenant Improvement</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="comm-message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Details
                </label>
                <textarea
                  id="comm-message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your commercial project..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-3.5 md:py-4 bg-[#D4AF37] text-white text-sm md:text-base font-semibold rounded-full hover:bg-[#B8941F] transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Request Consultation'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Thank you! Our commercial team will be in touch shortly.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    Something went wrong. Please try again or call us at 011 875 2075.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
